/**
 * The auth-session cache boundary: `me.get`'s query key is
 * identity-independent — identity travels in the Authorization header —
 * so cached data is only safe while exactly one identity owns the cache.
 * This module keys a {QueryClient, tRPC client} PAIR by an auth-session key
 * (stable provider uid — never the rotating token, never `me.id`).
 *
 * Two-phase replacement, matching React's render/commit split: `resolve`
 * (called during render) only RETIRES the previous pair — it never cancels or
 * clears, because React may discard that render and keep the committed tree
 * on the old client; destroying it there would wipe a live cache. The
 * provider drains retired pairs with `flushRetired` from a commit-phase
 * effect, at which point the swap is real and nothing observes them: a late
 * response from identity A lands in A's detached, soon-cleared cache, never
 * in B's.
 *
 * A pair swap alone is NOT enough for isolation: React Query binds each
 * mounted `useQuery` observer to the QueryClient of its component's first
 * render, so the provider must also REMOUNT the subtree (session-generation
 * `key`) or still-mounted observers keep serving the old identity's rows.
 *
 * Pure and dependency-injected: the React provider supplies the real pair
 * factory; tests drive it with fakes in node.
 */
import { REQUEST_ID_HEADER } from "@reflect/domain";

/** The teardown surface the cache needs from a query client. */
export interface TeardownQueryClient {
  cancelQueries(): Promise<void>;
  clear(): void;
}

/** What a session owns; torn down as one unit on identity change. */
export interface SessionPair<TQueryClient extends TeardownQueryClient, TClient> {
  queryClient: TQueryClient;
  trpcClient: TClient;
}

/** Session-keyed cache of the current pair. */
export interface SessionCache<TQueryClient extends TeardownQueryClient, TClient> {
  /**
   * The pair for `sessionKey`, reusing the current one when the key matches
   * (same-uid token refresh keeps the cache) and replacing it otherwise. The
   * replaced pair is only retired, never destroyed here — render-safe by
   * construction (Strict Mode re-renders and discarded renders included);
   * destruction happens in {@link flushRetired}.
   */
  resolve(sessionKey: string): SessionPair<TQueryClient, TClient>;
  /**
   * Cancel + clear every retired pair. Call from a commit-phase effect, when
   * the swap has committed and no observer can still read the old identity's
   * rows. Idempotent; no-ops when nothing is retired.
   */
  flushRetired(): void;
}

/**
 * Build a {@link SessionCache}.
 *
 * @param createPair - factory for a fresh pair (called once per session key).
 */
export function createSessionCache<TQueryClient extends TeardownQueryClient, TClient>(
  createPair: (sessionKey: string) => SessionPair<TQueryClient, TClient>,
): SessionCache<TQueryClient, TClient> {
  let current: { key: string; pair: SessionPair<TQueryClient, TClient> } | null = null;
  const retired: Array<SessionPair<TQueryClient, TClient>> = [];

  return {
    resolve(sessionKey) {
      if (current && current.key === sessionKey) return current.pair;
      // Retire, don't destroy: if React discards this render, the committed
      // tree keeps using the old pair untouched. Rapid A → B → A switches can
      // retire several pairs before an effect fires; all drain in one flush.
      if (current) retired.push(current.pair);
      current = { key: sessionKey, pair: createPair(sessionKey) };
      return current.pair;
    },

    flushRetired() {
      while (retired.length > 0) {
        const pair = retired.pop();
        if (!pair) break;
        // Cancellation is async (in-flight fetches); the synchronous clear
        // makes any late response land in a detached, empty cache.
        void pair.queryClient.cancelQueries().catch(() => undefined);
        pair.queryClient.clear();
      }
    },
  };
}

/**
 * Async-header factory for the tRPC link: injects the current bearer token
 * per request, plus a fresh correlation id — the API adopts it, stamps
 * logs/Sentry with it, and echoes it back, so a failing call's support id ties
 * the error screen to the exact server-side request (docs/debugging.md,
 * "Start from the correlation id").
 *
 * @param getIdToken - the token provider's token getter.
 */
export function requestHeaders(
  getIdToken: () => Promise<string | null>,
): () => Promise<{ authorization?: string; [REQUEST_ID_HEADER]: string }> {
  return async () => {
    const token = await getIdToken();
    return {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      [REQUEST_ID_HEADER]: crypto.randomUUID(),
    };
  };
}
